import type { Chat, ChatClientOptions } from "@storytell/cp-client";
import { onMount } from "solid-js";
import { createStore } from "solid-js/store";
import responses from "./conversation.json";
import {
  processChatRecording,
  processCompletedChat,
  processStreamingChat,
  startStreamingChat,
} from "./handlers/streamingChatHandlers";
import { cleanupChat, cleanupMessage, getChatStatus } from "./streamingChat";
import type {
  StreamingBlockId,
  StreamingChatId,
  StreamingMessageId,
  StreamingOperationId,
  StreamingStore,
} from "./streamingTypes";

const mockStream: (time: number) => AsyncGenerator<Chat.StreamResponse, void, unknown> = async function* (time) {
  for (const response of responses) {
    await new Promise((resolve) => setTimeout(resolve, time));
    yield response as unknown as Chat.StreamResponse;
  }
};
const mockCompletedStream = () => responses as unknown as Chat.StreamResponse[];

export const createStreamingStore = () => {
  const [state, setState] = createStore<StreamingStore>({
    // A chat is a collection of messages
    chats: {},
    // A message is a group of a prompt and a response made up
    // of a series of operations
    messages: {},
    // An operation is a single step in a message, made up of a series of blocks
    operations: {},
    // Blocks are the smallest unit of content in a message
    blocks: {},
  });
  // console.log("zzz state", state);

  // onMount(() => chat.processStreamingChat(mockStream(10)));
  // onMount(() => chat.processCompletedChat(mockCompletedStream()));
  // onMount(() => {
  //   // @ts-ignore
  //   window.trigger = () => {
  //     chat.processStreamingChat(mockStream(500));
  //   };
  // });
  // chat.processCompletedChat(mockCompletedStream());
  // onMount(() => {
  //   chat.processStreamingChat(mockStream(500));
  // });

  // TODO: @andi Split these out into separate files?
  const methods = {
    // CHAT METHODS
    chat: {
      startStreamingChat: (opts: {
        request: Chat.StreamRequest;
        options?: ChatClientOptions;
        onChatIdCaptured?: (chatId: StreamingChatId) => void;
        onMessageStarted?: (messageId: StreamingMessageId) => void;
        onMessageFinished?: (messageId: StreamingMessageId) => void;
      }) => startStreamingChat({ setStore: setState, ...opts }),
      /**
       * Process an async stream of chat messages
       * Use it for live streaming chats
       * @param stream - The async iterable stream of chat messages
       * @returns void
       */
      processStreamingChat: (stream: AsyncIterable<Chat.StreamResponse> | AsyncIterable<Chat.StreamPlaybackResponse>) =>
        processStreamingChat(setState, stream),
      /**
       * Process a completed chat
       * Use it for saved chats, will perform synchronous processing
       * @param stream - The stream of chat messages
       * @returns void
       */
      processCompletedChat: (stream: Iterable<Chat.StreamResponse | Chat.StreamPlaybackResponse>) =>
        processCompletedChat(setState, stream),
      /**
       * Process a chat recording
       * Use it for saved chats, will perform synchronous processing
       * @param recording - The chat recording
       * @returns void
       */
      processChatRecording: (recording: Chat.ConversationResponse) => processChatRecording(setState, recording),

      get: (chatId: StreamingChatId) => state.chats[chatId],
      getStatus: (chatId: StreamingChatId) => getChatStatus(state, chatId),

      cleanup: (chatId: StreamingChatId) => cleanupChat(setState, chatId),
    },

    // MESSAGE METHODS
    message: {
      get: (messageId: StreamingMessageId) => state.messages[messageId],
      cleanup: (messageId: StreamingMessageId) => cleanupMessage(setState, messageId),
    },

    // OPERATION METHODS
    operation: {
      get: (operationId: StreamingOperationId) => state.operations[operationId],
    },

    // BLOCK METHODS
    block: {
      get: (blockId: StreamingBlockId) => state.blocks[blockId],
    },
  };

  return {
    state,
    hydrate: setState,
    ...methods,
  } as const;
};

export type StreamingService = ReturnType<typeof createStreamingStore>;
